import {
  Alert,
  Button,
  Card,
  CardBody,
  TextField,
  Typography,
} from '@statsbomb/kitbag-components';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { styled } from 'styled-components';
import { REACT_APP_AUTH_ROLE_OPERATOR } from '../../AuthProvider';
import { selectedMetadataAtom } from '../../atoms/selectedMetadata';
import { selectedSourceDataAtom } from '../../atoms/selectedSourceData';
import {
  addUploadInProgressAtom,
  incrementCompletedAtom,
  incrementInProgressAtom,
  updateUploadAtom,
} from '../../atoms/uploads';
import useApi, { API_ENDPOINTS } from '../../hooks/api';
import { sourceSerialiser } from '../../serialisers/sourceData';
import { videoReferenceSerialiser } from '../../serialisers/videoReference';
import { logError } from '../../utils/errors';
import themeHelper from '../../utils/themeHelper';
import ErrorText from '../ErrorText';
import CardHeader from '../CardHeader';
import IngestAndConvert from '../IngestAndConvert';
import MetadataSelection from '../MetadataSelection';
import Secured from '../Secured';
import { SelectSource } from '../SelectSource/SelectSource';
import { MANUAL_UPLOAD } from '../SelectSource/sourceOptions';
import {
  DEFAULT_QUALITY_SETTINGS,
  VideoQualitySettings,
} from '../VideoQualitySettings/VideoQualitySettings';

export const FILE_CHUNK_SIZE = 100000000; // 100MB

const StyledFileUpload = styled.input`
  color: transparent;
  display: 'block';
  padding: ${themeHelper.spacing(0, 1)};
`;

const StyledContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-start;
  gap: ${themeHelper.spacing()};
  padding: ${themeHelper.spacing(1, 0)};
`;

export function Ingest() {
  const api = useApi();
  const intl = useIntl();

  const UPLOAD_INITIATED_MESSAGE = intl.formatMessage({
    defaultMessage: 'Upload In Progress',
    id: 'cb98d',
  });
  const UPLOAD_FAILURE_MESSAGE = intl.formatMessage({
    defaultMessage: 'Upload Failed',
    id: '632df',
  });
  const UPLOAD_COMPLETED_MESSAGE = intl.formatMessage({
    defaultMessage: 'Upload Completed',
    id: 'bfa3d',
  });
  const INITIATING_MESSAGE = intl.formatMessage({
    defaultMessage: 'Upload initiating. Please keep your browser window open',
    id: '010eb',
  });
  const FAILED_VALIDATION_MESSAGE = intl.formatMessage({
    defaultMessage: 'Check input values and resubmit',
    id: '1ec69',
  });
  const SELECT_FILE_OR_URL_MESSAGE = intl.formatMessage({
    defaultMessage: 'Please select a file or input a URL',
    id: 'c8d18',
  });
  const DOWNLOAD_INITIATED_MESSAGE = intl.formatMessage({
    defaultMessage: 'Download into Statsbomb cloud initiated',
    id: '40f88',
  });

  const addUpload = useSetAtom(addUploadInProgressAtom);
  const updateUpload = useSetAtom(updateUploadAtom);
  const incrementInProgress = useSetAtom(incrementInProgressAtom);
  const incrementComplete = useSetAtom(incrementCompletedAtom);

  const [currentFileSelection, setCurrentFileSelection] = useState();

  const sourceData = useAtomValue(selectedSourceDataAtom);

  const [url, setUrl] = useState('');
  const [localMessage, setLocalMessage] = useState('');
  const [localMessageType, setLocalMessageType] = useState('');

  const selectedMetadata = useAtomValue(selectedMetadataAtom);
  const {
    sport,
    matchId,
    period: matchPart,
    version,
    track,
  } = selectedMetadata;

  const isUploadViaUrl = sourceData?.uploadMethod !== MANUAL_UPLOAD.id;
  const displayVideoQualitySettings = sourceData?.requiresTranscoding;

  const [qualitySettings, setQualitySettings] = useState({
    bitrate: DEFAULT_QUALITY_SETTINGS.MAX_BITRATE,
    framerate: DEFAULT_QUALITY_SETTINGS.FRAMERATE,
    resolution: DEFAULT_QUALITY_SETTINGS.RESOLUTION,
  });

  const filename = `${sport}_${matchId}_${matchPart}_${version}_${track}`;

  const videoReference = {
    matchId,
    matchPart,
    sport,
    track,
    version,
  };

  const uploadFromMachine = async (fileToUpload, videoReferenceToUpload) => {
    setLocalMessage(INITIATING_MESSAGE);
    addUpload({
      filename: fileToUpload.name,
      message: UPLOAD_INITIATED_MESSAGE,
    });

    // Create
    let uploadId;
    try {
      const response = await api.get(
        `${API_ENDPOINTS.videoService}/multipartupload/create`,
        {
          params: {
            filename: fileToUpload.name,
            filetype: fileToUpload.type,
            ...videoReferenceSerialiser(videoReferenceToUpload),
          },
        },
      );
      uploadId = response.data.upload_id;
    } catch (err) {
      updateUpload({
        filename: fileToUpload.name,
        message: UPLOAD_FAILURE_MESSAGE,
      });
      logError(err);
    }

    // Upload chunks
    const uploadPartsArray = [];
    try {
      const fileSize = fileToUpload.size;
      updateUpload({ filename: fileToUpload.name, size: fileSize });
      const NUM_CHUNKS = Math.floor(fileSize / FILE_CHUNK_SIZE) + 1;
      let start;
      let end;
      let blob;
      const promisesArray = [];

      // eslint-disable-next-line no-plusplus
      for (let index = 1; index < NUM_CHUNKS + 1; index++) {
        incrementInProgress(fileToUpload.name);
        start = (index - 1) * FILE_CHUNK_SIZE;
        end = index * FILE_CHUNK_SIZE;
        blob =
          index < NUM_CHUNKS
            ? fileToUpload.slice(start, end)
            : fileToUpload.slice(start);

        // eslint-disable-next-line no-await-in-loop
        const getUploadUrlResp = await api.get(
          `${API_ENDPOINTS.videoService}/multipartupload/part/url`,
          {
            params: {
              filename: fileToUpload.name,
              part_no: index,
              upload_id: uploadId,
              ...videoReferenceSerialiser(videoReferenceToUpload),
            },
          },
        );

        const { presigned_url: preSignedUrl } = getUploadUrlResp.data;
        const uploadResp = api
          .put(preSignedUrl, {
            data: blob,
            headers: { 'Content-Type': fileToUpload.type },
          })
          .then((response) => {
            incrementComplete(fileToUpload.name);
            return response;
          });
        promisesArray.push(uploadResp);
      }

      const resolvedArray = await Promise.all(promisesArray);

      resolvedArray.forEach((resolvedPromise, index) => {
        uploadPartsArray.push({
          ETag: resolvedPromise?.headers.etag,
          PartNumber: index + 1,
        });
      });
    } catch (err) {
      logError(err);
    }

    // Complete Upload
    try {
      const completeUploadResp = await api.post(
        `${API_ENDPOINTS.videoService}/multipartupload/complete`,
        {
          data: JSON.stringify({
            filename: fileToUpload.name,
            parts: uploadPartsArray,
            upload_id: uploadId,
            ...videoReferenceSerialiser(videoReferenceToUpload),
            ...sourceSerialiser(sourceData),
          }),
        },
      );

      updateUpload({
        filename: fileToUpload.name,
        message:
          completeUploadResp?.status === 200
            ? UPLOAD_COMPLETED_MESSAGE
            : UPLOAD_FAILURE_MESSAGE,
      });
      setLocalMessage('');
    } catch (err) {
      logError(err);
    }
  };

  const fileChangedHandler = (event) => {
    try {
      setCurrentFileSelection(event.target.files[0]);
    } catch (err) {
      logError(err);
    }
  };

  const validateInputs = () => {
    if (!url?.length && !currentFileSelection?.name) {
      setLocalMessage(SELECT_FILE_OR_URL_MESSAGE);
      setLocalMessageType('error');
      return false;
    }

    const validation =
      matchId &&
      parseInt(matchId, 10) > 0 &&
      matchPart &&
      parseInt(matchPart, 10) > 0 &&
      sourceData.sourceName;
    if (!validation) {
      setLocalMessage(FAILED_VALIDATION_MESSAGE);
      setLocalMessageType('error');
    } else {
      setLocalMessage(undefined);
      setLocalMessageType(undefined);
    }

    return !!validation;
  };

  const handleStart = async () => {
    if (validateInputs()) {
      if (currentFileSelection?.name && !isUploadViaUrl) {
        uploadFromMachine(currentFileSelection, videoReference);
      } else {
        try {
          await api.post(`${API_ENDPOINTS.videoService}/downloads`, {
            data: {
              url,
              ...videoReferenceSerialiser(videoReference),
              ...sourceSerialiser(sourceData),
            },
          });
        } catch (err) {
          logError(err);
        }
        setLocalMessage(DOWNLOAD_INITIATED_MESSAGE);
      }
    }
  };

  /**
   * On error display the error message in the form.
   *
   * @param {String} localisedErrorMessage
   */
  const handleIngestError = (localisedErrorMessage) => {
    setLocalMessage(localisedErrorMessage);
    setLocalMessageType('error');
  };

  /**
   * When the api call has been sent (job probably not completed)
   */
  const handleIngestSent = () => {
    setLocalMessage(
      intl.formatMessage({
        defaultMessage: 'Video sent for ingestion and conversion.',
        id: '4073e',
      }),
    );
    setLocalMessageType('success');
  };

  /**
   * Called before the ingest API fires off a request.
   * So validate the form and return false if not valid.
   *
   * @returns {boolean}
   */
  const handleIngestStart = () => validateInputs();

  const handleUrlChange = (event) => setUrl(event.target.value);

  return (
    <Card>
      <CardHeader>
        <FormattedMessage
          defaultMessage="Upload Video Into Library"
          id="a963a"
        />
      </CardHeader>
      <CardBody>
        {!api.isFetching && api.errorMessage && (
          <ErrorText errorMessage={api.errorMessage} />
        )}
        {localMessage && !api.errorMessage && (
          <Alert type={localMessageType || 'info'}>
            <Typography variant="bodyRegular">{localMessage}</Typography>
          </Alert>
        )}
        <form>
          <MetadataSelection />
          <SelectSource sport={sport} />
          <StyledContainer>
            {!isUploadViaUrl ? (
              <label htmlFor="file-upload">
                <Typography variant="bodySmall" as="span">
                  {currentFileSelection?.name || (
                    <FormattedMessage defaultMessage="File Upload" id="99e8e" />
                  )}
                </Typography>
                <StyledFileUpload
                  id="file-upload"
                  onChange={fileChangedHandler}
                  type="file"
                />
              </label>
            ) : (
              <TextField
                id="url-input-basic"
                label={intl.formatMessage({
                  defaultMessage: 'URL',
                  id: 'e6b39',
                })}
                onChange={handleUrlChange}
                placeholder={intl.formatMessage({
                  defaultMessage: 'URL',
                  id: 'e6b39',
                })}
                value={url}
                variant="secondary"
              />
            )}
            <Secured allowed={REACT_APP_AUTH_ROLE_OPERATOR}>
              {displayVideoQualitySettings && isUploadViaUrl && (
                <VideoQualitySettings
                  qualitySettings={qualitySettings}
                  setQualitySettings={setQualitySettings}
                />
              )}
            </Secured>
          </StyledContainer>
          <div>
            {displayVideoQualitySettings ? (
              <IngestAndConvert
                filename={filename}
                onError={handleIngestError}
                onStart={handleIngestStart}
                onSent={handleIngestSent}
                qualitySettings={qualitySettings}
                sourceData={sourceData}
                urls={[url]}
                videoReference={videoReference}
              >
                <FormattedMessage defaultMessage="Start Upload" id="d6f4f" />
              </IngestAndConvert>
            ) : (
              <Button onClick={handleStart} variant="primary">
                <FormattedMessage defaultMessage="Start Upload" id="d6f4f" />
              </Button>
            )}
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

export default { Ingest };
